import { updateSearchContext } from "@tm/insurance/analytics/xdm";
import { convertDateToUnix } from "@tm/insurance/utils";
import { SelectCountryModalV2 } from "@tm/static";
import { usePlanType } from "@tm/ui";
import { useCallback, useEffect, useRef } from "react";
import CalendarDropdown from "../../../../../components/CommonLandingDT/CalendarDropdown";
import LandingButton from "../../../../../components/LandingButton/LandingButton";
import {
  trackListingClick,
  trackListingOmniture,
  trackListingView,
} from "../../trackingUtils";
import {
  CalendarDropdownWrapper,
  ContextEditBar,
  ContextEditInner,
  CountryDropdownContainer,
  DropdownContainer,
  FieldsRow,
  FieldWrapper,
  SearchButtonWrap,
} from "./ContextEdit.styles";
import { ContextEditProps } from "./ContextEdit.types";
import InputField from "./InputField";
import TravellerDropdown from "./TravellerDropdown";
import { useContextEditState } from "./useContextEditState";

const ContextEdit = (props: ContextEditProps) => {
  const { travelDetails, cta, pageTracked } = props;
  const countryListConfig = travelDetails?.countries?.countryList;
  const { type } = usePlanType();

  const {
    countryData,
    setCountryData,
    popularCountries,
    setPopularCountries,
    showCountry,
    setShowCountry,
    showOtherCountry,
    handleShowOtherCountry,
    displayCountries,
    travelNameRef,
    isCountryError,
    setIsCountryError,

    range,
    tempRange,
    openCalendar,
    disabledDays,
    handleDayClick,
    handleSingleDateSelect,
    handleStartDateClick,
    handleEndDateClick,
    startCalendarRef,
    endCalendarRef,
    isSingleDate,
    currentMonth,
    handleMonthChange,
    weekDayNameFormatter,
    monthNameFormatter,

    travellers,
    showTravellerDropdown,
    handleShowTravellerDropdown,
    handleTravellerSelect,
    travellerDropdownRef,

    showToast,
    toastMessage,

    formatDate,
    handleSearch,
    selectedCountryCodes,
  } = useContextEditState(props);

  const handleDescriptionModal = useCallback(() => "", []);

  const handleCountryOmniture = useCallback(
    (event: { actionType: string; payload?: string }, eventType: string) => {
      trackListingOmniture(event, eventType, type);
    },
    [type]
  );

  const handleCalendarClick = useCallback(
    (openFn: () => void, actionType: string) => {
      trackListingClick(actionType, type);
      openFn();
    },
    [type]
  );

  const handleTravellerFieldClick = useCallback(() => {
    trackListingClick("listing_page_travellers_clicked", type);
    handleShowTravellerDropdown();
  }, [handleShowTravellerDropdown, type]);

  const handleCountryFieldClick = useCallback(() => {
    trackListingClick("listing_page_destination_clicked", type);
    handleShowOtherCountry();
  }, [handleShowOtherCountry, type]);

  const handleCountrySelect = useCallback(() => {
    const selectedCodes = selectedCountryCodes?.join(",") || "";
    trackListingClick(`listing_page_destination_edited_${selectedCodes}`, type);
    handleShowOtherCountry();
  }, [handleShowOtherCountry, selectedCountryCodes, type]);

  const handleSearchClick = useCallback(() => {
    trackListingClick("search_clicked", type);
    try {
      handleSearch();
    } catch (error) {
      console.error("Context edit search failed", error);
    }
  }, [handleSearch, type]);

  const hasMountedTravellers = useRef(false);
  useEffect(() => {
    if (!hasMountedTravellers.current) {
      hasMountedTravellers.current = true;
      return;
    }
    trackListingClick("listing_page_travellers_edited", type);
  }, [travellers]);

  const hasMountedDates = useRef(false);
  useEffect(() => {
    if (!hasMountedDates.current) {
      hasMountedDates.current = true;
      return;
    }
    if (!range?.from || !range?.to) return;
    trackListingClick("listing_page_travel_duration_edited", type);
  }, [range?.from?.getTime(), range?.to?.getTime()]);

  // ──── Update search context on param changes ────
  useEffect(() => {
    if (!range?.from || !range?.to || !selectedCountryCodes?.length) return;
    try {
      updateSearchContext({
        countries: selectedCountryCodes as any,
        travellers,
        fromDateTime: convertDateToUnix(range.from),
        toDateTime: convertDateToUnix(range.to),
      });
    } catch (err) {
      console.error("Search context update failed", err);
    }
  }, [selectedCountryCodes, range, travellers]);

  // ──── Component View: search context shown ────
  useEffect(() => {
    if (!pageTracked) return;
    trackListingView("listing_page_search_context_shown", type);
  }, [pageTracked, type]);

  return (
    <ContextEditBar>
      <ContextEditInner>
        <FieldsRow>
          {/* ──── Country ──── */}
          <FieldWrapper ref={travelNameRef}>
            <InputField
              label={travelDetails?.countries?.title || "TRAVELLING TO"}
              value={displayCountries}
              placeholder={countryListConfig?.placeHolder}
              onClick={handleCountryFieldClick}
              isActive={showOtherCountry}
              flex
              hasError={isCountryError}
              errorMessage={countryListConfig?.error_message}
            />
            {showOtherCountry && (
              <CountryDropdownContainer>
                {/* eslint-disable @typescript-eslint/no-explicit-any */}
                <SelectCountryModalV2
                  handleSelectCountry={handleCountrySelect}
                  countryData={countryData as any[]}
                  countrySelectErrMsg={
                    countryListConfig?.countrySelectErrMsg || ""
                  }
                  showCountry={showCountry as any[]}
                  setShowCountry={setShowCountry as any}
                  setIsCountryError={setIsCountryError}
                  popularCountries={popularCountries as any[]}
                  handleDescriptionModal={handleDescriptionModal}
                  setCountryData={setCountryData as any}
                  setPopularCountries={setPopularCountries as any}
                  focusSearchOnOpen
                  isDropdown
                  travelNameRef={travelNameRef}
                  onTrackOmniture={handleCountryOmniture}
                />
                {/* eslint-enable @typescript-eslint/no-explicit-any */}
              </CountryDropdownContainer>
            )}
          </FieldWrapper>

          {/* ──── Start Date ──── */}
          <FieldWrapper>
            <InputField
              label={travelDetails?.dates?.startDateLabel || "START DATE"}
              value={formatDate(range?.from)}
              onClick={() =>
                handleCalendarClick(
                  handleStartDateClick,
                  "listing_page_start_date_clicked"
                )
              }
              isActive={openCalendar === "start"}
            />
            {openCalendar === "start" && (
              <CalendarDropdownWrapper>
                <CalendarDropdown
                  calendarRef={startCalendarRef}
                  isSingleDate={isSingleDate}
                  selectedSegment={null}
                  showToast={showToast}
                  toastMessage={toastMessage}
                  range={range}
                  tempRange={tempRange}
                  openCalendar={openCalendar}
                  disabledDays={disabledDays}
                  handleSingleDateSelect={handleSingleDateSelect}
                  handleDayClick={handleDayClick}
                  weekDayNameFormatter={weekDayNameFormatter}
                  monthNameFormatter={monthNameFormatter}
                  currentMonth={currentMonth}
                  handleMonthChange={handleMonthChange}
                />
              </CalendarDropdownWrapper>
            )}
          </FieldWrapper>

          {/* ──── End Date ──── */}
          <FieldWrapper data-disabled={isSingleDate}>
            <InputField
              label={travelDetails?.dates?.endDateLabel || "END DATE"}
              value={formatDate(range?.to)}
              onClick={() =>
                handleCalendarClick(
                  handleEndDateClick,
                  "listing_page_end_date_clicked"
                )
              }
              isActive={openCalendar === "end"}
              disabled={isSingleDate}
            />
            {openCalendar === "end" && (
              <CalendarDropdownWrapper>
                <CalendarDropdown
                  calendarRef={endCalendarRef}
                  isSingleDate={false}
                  selectedSegment={null}
                  showToast={showToast}
                  toastMessage={toastMessage}
                  range={range}
                  tempRange={tempRange}
                  openCalendar={openCalendar}
                  disabledDays={disabledDays}
                  handleSingleDateSelect={handleSingleDateSelect}
                  handleDayClick={handleDayClick}
                  weekDayNameFormatter={weekDayNameFormatter}
                  monthNameFormatter={monthNameFormatter}
                  currentMonth={currentMonth}
                  handleMonthChange={handleMonthChange}
                />
              </CalendarDropdownWrapper>
            )}
          </FieldWrapper>

          {/* ──── Travellers ──── */}
          <FieldWrapper>
            <InputField
              label={travelDetails?.travellerCount?.title || "TRAVELERS"}
              value={travellers <= 9 ? `0${travellers}` : String(travellers)}
              onClick={handleTravellerFieldClick}
              isActive={showTravellerDropdown}
            />
            {showTravellerDropdown && (
              <DropdownContainer>
                <TravellerDropdown
                  dropdownRef={travellerDropdownRef}
                  count={travellers}
                  onSelect={handleTravellerSelect}
                />
              </DropdownContainer>
            )}
          </FieldWrapper>
        </FieldsRow>

        <SearchButtonWrap>
          <LandingButton
            customTitle={cta?.expandedView || "SEARCH"}
            customWidth="100%"
            customHeight="44px"
            onClick={handleSearchClick}
          />
        </SearchButtonWrap>
      </ContextEditInner>
    </ContextEditBar>
  );
};

export default ContextEdit;
